import React, { useState, useEffect } from 'react';
import { API } from '../utils/api';

import LucideIcon from './LucideIcon';
import RankRing from './gamification/RankRing';
import StreakChip from './gamification/StreakChip';
import { getRankName } from '../utils/iconMapper';
import { computeBadges, RARITY_STYLE } from '../config/badges';

interface SkillProgress {
  level: number;
  progress: number;
  totalChallenges: number;
  completedChallenges: number;
}

interface UserProfile {
  username: string;
  email: string;
  level: number;
  points: number;
  nextLevelPoints: number;
  streak: number;
  challengesCompleted: number;
  questsCompleted: number;
  questLevelsCompleted: number;
  averageScore: number;
  isAdmin: boolean;
  completedQuests: string[];
  skills: {
    [key: string]: SkillProgress;
  };
  badges: {
    id: string;
    name: string;
    icon: string;
    description: string;
    earnedAt?: string;
  }[];
}

interface Activity {
  id: string;
  type: 'challenge_complete' | 'level_up' | 'badge_earned';
  title: string;
  description: string;
  points?: number;
  timestamp: string;
  success?: boolean;
}

const TOPIC_NAMES: { [key: string]: string } = {
  'basic-prompt-engineering': 'Basic Prompt Engineering',
  'writing-editing': 'Writing & Editing',
  'summarizing-research': 'Summarizing & Research Synthesis',
  'data-analysis': 'Data Analysis & Structured Output',
  'coding-debugging': 'Coding & Debugging Assistance',
  'tutoring-explaining': 'Tutoring & Explaining Concepts',
  'customer-communication': 'Customer & Client Communication',
  'planning-decisions': 'Planning & Decision Support',
  'creative-ideation': 'Creative & Content Ideation',
  'agent-design': 'Designing AI Agents & Workflows',
};

function getActivityIcon(type: Activity['type']) {
  switch (type) {
    case 'challenge_complete': return 'status-check';
    case 'level_up': return 'TrendingUp';
    case 'badge_earned': return 'Sparkles';
    default: return 'ScrollText';
  }
}

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile>({
    username: 'User', email: 'user@example.com', level: 1, points: 0, nextLevelPoints: 1000,
    streak: 0, challengesCompleted: 0, questsCompleted: 0, questLevelsCompleted: 0, averageScore: 0,
    isAdmin: false, completedQuests: [], skills: {}, badges: [],
  });
  const [activities, setActivities] = useState<Activity[]>([]);

  const loadProfile = async () => {
    try {
      const response = await API.get('/profile');
      if (response) {
        setProfile({
          ...response,
          username: response.username || 'User',
          email: response.email || 'user@example.com',
          isAdmin: response.isAdmin || false,
          skills: response.skills || {},
        });
      }
    } catch (error) {
      // Error loading profile -- the initial state above already covers rendering.
    }
  };

  const loadActivities = async () => {
    try {
      const response = await API.get('/profile/activities');
      if (response && response.activities) {
        setActivities(response.activities);
      }
    } catch (error) {
      // Error loading activities
    }
  };

  useEffect(() => {
    loadProfile();
    loadActivities();

    const handleChallengeCompleted = (event: CustomEvent) => {
      const challengeData = event.detail;
      setProfile((prev) => ({
        ...prev,
        points: prev.points + challengeData.points,
        challengesCompleted: prev.challengesCompleted + 1,
      }));
      setActivities((prev) => [
        {
          id: Date.now().toString(),
          type: 'challenge_complete',
          title: 'Challenge Completed',
          description: `Completed "${challengeData.challengeId}" challenge`,
          points: challengeData.points,
          timestamp: new Date().toLocaleDateString(),
          success: true,
        },
        ...prev,
      ]);
      setTimeout(() => { loadProfile(); loadActivities(); }, 2000);
    };

    window.addEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
    return () => window.removeEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
  }, []);

  window.refreshProfile = () => { loadProfile(); loadActivities(); };

  const rankProgress = profile.nextLevelPoints > 0 ? Math.min(100, Math.round((profile.points / profile.nextLevelPoints) * 100)) : 100;
  const initials = profile.username.slice(0, 2).toUpperCase();
  const skillEntries = Object.entries(profile.skills).filter(([, skill]) => skill.completedChallenges > 0);

  const earnedBadgeNames = profile.badges.map((b) => (typeof b === 'string' ? b : b.name));
  const earnedBadges = computeBadges(
    { challengesCompleted: profile.challengesCompleted, streak: profile.streak, points: profile.points, level: profile.level, questsCompleted: profile.questsCompleted },
    profile.completedQuests,
    earnedBadgeNames
  ).filter((b) => b.earned);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 900 }}>
      {/* Identity */}
      <div className="ws-fade-up" style={{ ...panel, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--ember)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
          <span className="ws-display" style={{ fontSize: 22, fontWeight: 700 }}>{initials}</span>
        </div>
        <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 className="ws-display" style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{profile.username}</h1>
            {profile.isAdmin && (
              <span className="ws-mono" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ember-strong)', background: 'var(--ember-soft)', border: '1px solid var(--ember)', borderRadius: 999, padding: '3px 10px' }}>
                Admin
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>{profile.email}</p>
          <div style={{ marginTop: 4 }}><StreakChip days={profile.streak} /></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: '0 0 auto' }}>
          <RankRing level={profile.level} progress={rankProgress} size={56} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <span className="ws-display" style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{getRankName(profile.level)}</span>
            <span className="ws-mono" style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{profile.points}/{profile.nextLevelPoints} XP</span>
          </div>
        </div>
      </div>

      {/* Stat strip -- same pattern as Dashboard's, so the two pages read as one app */}
      <div className="ws-fade-up ws-profile-stats" style={{ display: 'flex', alignItems: 'stretch', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '6px 8px', animationDelay: '60ms', flexWrap: 'wrap' }}>
        {[
          { icon: 'missions', value: profile.challengesCompleted, label: 'Missions completed' },
          { icon: 'graded-info', value: `${profile.averageScore}/100`, label: 'Avg challenge score' },
          { icon: 'quest', value: `${profile.questsCompleted}/5`, label: 'Quests' },
          { icon: 'quest-trials', value: `${profile.questLevelsCompleted}/50`, label: 'Trials mastered' },
        ].map((stat, i, arr) => (
          <React.Fragment key={stat.label}>
            <div style={{ flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px' }}>
              <LucideIcon name={stat.icon} size={17} />
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                <span className="ws-display" style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{stat.value}</span>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{stat.label}</span>
              </div>
            </div>
            {i < arr.length - 1 && <div style={{ width: 1, background: 'var(--border)', margin: '6px 0' }} />}
          </React.Fragment>
        ))}
      </div>

      {/* Skills */}
      <div className="ws-fade-up" style={{ ...panel, animationDelay: '100ms' }}>
        <h2 className="ws-display" style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Skills</h2>
        {skillEntries.length === 0 ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-tertiary)', textAlign: 'center', padding: '20px 0' }}>
            Complete a lesson and it&rsquo;ll show up here.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {skillEntries.map(([skillName, skill]) => (
              <div key={skillName}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6, gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {TOPIC_NAMES[skillName] || skillName}
                    <span className="ws-mono" style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 700, color: 'var(--ember-strong)' }}>Level {skill.level}</span>
                  </span>
                  <span className="ws-mono" style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{skill.completedChallenges}/{skill.totalChallenges} challenges</span>
                </div>
                <div style={{ height: 8, borderRadius: 999, background: 'var(--surface-sunken)', border: '1px solid var(--border)', overflow: 'hidden' }}>
                  <div style={{ width: `${skill.progress}%`, height: '100%', borderRadius: 999, background: 'var(--ember)' }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mission Log */}
      <div className="ws-fade-up" style={{ ...panel, animationDelay: '140ms' }}>
        <h2 className="ws-display" style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Mission log</h2>
        {activities.length === 0 ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-tertiary)', textAlign: 'center', padding: '20px 0' }}>
            Nothing here yet — finish a lesson and it&rsquo;ll show up in this list.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activities.map((activity) => (
              <div key={activity.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', borderRadius: 10, background: 'var(--surface-sunken)' }}>
                <span style={{ color: 'var(--ember-strong)', flex: '0 0 auto', marginTop: 1 }}><LucideIcon name={getActivityIcon(activity.type)} size={18} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{activity.title}</span>
                    {activity.points ? (
                      <span className="ws-mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ember-strong)' }}>+{activity.points} pts</span>
                    ) : null}
                  </div>
                  <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>{activity.description}</p>
                  <span className="ws-mono" style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{activity.timestamp}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Honors & Badges */}
      <div className="ws-fade-up" style={{ ...panel, animationDelay: '180ms' }}>
        <h2 className="ws-display" style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Honors &amp; badges</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
          {earnedBadges.map((badge) => {
            const style = RARITY_STYLE[badge.rarity] || RARITY_STYLE.common;
            return (
              <div key={badge.id} style={{ textAlign: 'center', padding: '16px 12px', borderRadius: 12, border: `1.5px solid ${style.border}`, background: style.bg }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, color: style.fg }}><LucideIcon name={badge.icon} size={26} /></div>
                <p style={{ margin: '0 0 3px', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{badge.name}</p>
                <p style={{ margin: '0 0 8px', fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{badge.description}</p>
                <span className="ws-mono" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: style.fg }}>{badge.rarity}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Profile;
